import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import CountrySelect from "@/components/circle/CountrySelect";
import { BackButton, Consent, ErrorLine, Field, NextButton, Option, StepHead } from "@/components/circle/Parts";
import { COMMS, INTERESTS, RANGES } from "@/lib/circle";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const PrivacyLink = () => <Link to="/legal/privacy" target="_blank" className="underline underline-offset-2 hover:text-forest">Privacy Policy</Link>;

export function StepOne({ form, set, onSubmit, busy, error }) {
    return (
        <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="ic-step-1">
            <StepHead title="Let's get acquainted." sub="A few details to join our circle." />
            <div className="grid grid-cols-2 gap-4">
                <Field label="First name" htmlFor="ic-first"><input id="ic-first" required autoComplete="given-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="editorial-input" data-testid="ic-first-name" /></Field>
                <Field label="Last name" htmlFor="ic-last"><input id="ic-last" required autoComplete="family-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="editorial-input" data-testid="ic-last-name" /></Field>
            </div>
            <Field label="Email address" htmlFor="ic-email"><input id="ic-email" type="email" required autoComplete="email" inputMode="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="editorial-input" data-testid="ic-email" /></Field>
            <Field label="Country of residence" htmlFor="ic-country"><CountrySelect id="ic-country" value={form.country} onChange={(v) => set("country", v)} /></Field>
            <Consent checked={form.privacy_ack} onChange={(v) => set("privacy_ack", v)} testid="ic-privacy">
                I have read the <PrivacyLink /> and agree that Verde 2050 processes my details to register me for The Investor Circle.
            </Consent>
            <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <ErrorLine error={error} />
            <NextButton busy={busy} testid="ic-step1-continue">Continue</NextButton>
            <p className="flex items-center gap-2 text-xs text-muted_text" data-testid="ic-private-note"><Lock className="h-3.5 w-3.5" /> Your information stays private. No spam.</p>
        </form>
    );
}

export function StepTwo({ form, set, onSubmit, onBack, busy, error }) {
    return (
        <form onSubmit={onSubmit} noValidate className="space-y-7" data-testid="ic-step-2">
            <StepHead title="What interests you?" sub="So we can share opportunities that are relevant to you." />
            <fieldset>
                <legend className="text-sm text-ink mb-3">Which opportunities interest you?</legend>
                <div role="radiogroup" className="grid grid-cols-2 gap-2.5">
                    {INTERESTS.map((o) => <Option key={o} selected={form.interest === o} onClick={() => set("interest", o)} testid={`ic-interest-${slug(o)}`}>{o}</Option>)}
                </div>
            </fieldset>
            <fieldset>
                <legend className="text-sm text-ink mb-3">What amount might you consider investing?</legend>
                <div role="radiogroup" className="grid grid-cols-2 gap-2">
                    {RANGES.map((o) => <Option compact key={o} selected={form.amount_range === o} onClick={() => set("amount_range", o)} testid={`ic-range-${slug(o)}`}>{o}</Option>)}
                </div>
                <p className="mt-3 text-xs text-muted_text">No minimum commitment. This simply helps us understand your interests.</p>
            </fieldset>
            <ErrorLine error={error} />
            <div className="flex items-center justify-between gap-3"><BackButton onClick={onBack} /><NextButton busy={busy} testid="ic-step2-continue">Continue</NextButton></div>
        </form>
    );
}

export function StepThree({ form, set, onSubmit, onBack, busy, error }) {
    const choice = COMMS.find(([k]) => k === form.communication)?.[1];
    return (
        <form onSubmit={onSubmit} noValidate className="space-y-6" data-testid="ic-step-3">
            <StepHead title="You're almost in." sub="How would you like to hear from us?" />
            <fieldset>
                <legend className="text-sm text-ink mb-3">What would you like to receive?</legend>
                <div role="radiogroup" className="space-y-2.5">
                    {COMMS.map(([k, label]) => <Option key={k} selected={form.communication === k} onClick={() => set("communication", k)} testid={`ic-comms-${k}`}>{label}</Option>)}
                </div>
            </fieldset>
            <Field label="Anything else you'd like us to know?" htmlFor="ic-notes">
                <textarea id="ic-notes" rows={3} maxLength={2000} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional — tell us a little about your interests." className="editorial-input resize-y" data-testid="ic-notes" />
            </Field>
            <Consent checked={form.marketing_consent} onChange={(v) => set("marketing_consent", v)} testid="ic-marketing-consent">
                I agree to receive emails from Verde 2050 with {choice ? choice.replace(/\.$/, "").toLowerCase() : "the communications selected above"}. I can unsubscribe at any time using the link in every email. See our <PrivacyLink />.
            </Consent>
            <ErrorLine error={error} />
            <div className="flex items-center justify-between gap-3"><BackButton onClick={onBack} /><NextButton busy={busy} testid="ic-submit">Join the Investor Circle</NextButton></div>
        </form>
    );
}
